import { computed } from "vue";

import { useNewEstimateStore } from "@/stores/new/estimateStore";
import { usePartitionStore } from "@/stores/new/partition.store";
import { useDoorsStore } from "@/stores/new/doors.store";
import { useOverdoorStore } from "@/stores/new/overdoor.store";
import { useSpacesStore } from "@/stores/new/spaces.store";
import { useOverheadStore } from "@/stores/new/overhead.store";
import { useMountsStore } from "@/stores/new/mounts.store";
import { useExtraStore } from "@/stores/new/extra.store";
import { useInstallationStore } from "@/stores/new/installation.store";
import { useDeliveryStore } from "@/stores/new/delivery.store";
import { useEstimateReviewStore } from "@/stores/new/estimateReview.store";

import {
    DoorCut,
    ExtraItem,
    Partition,
    Pricing,
    ReactiveComponent,
    Space,
} from "@/app/domain";
import {
    ICalculationStrategy,
    GlassCalculationStrategy,
    GlassPricingStrategy,
    OutOfSquareCalculationStrategy,
    OutOfSquarePricingStrategy,
    IPricingStrategy,
    PriceResult,
    GlassDoorPricingStrategy,
    GlassDoorCalculationStrategy,
    DoorHardwarePricingStrategy,
    DoorHardwareCalculationStrategy,
    DoorCutsPricingStrategy,
    DoorCalculationStrategy,
    DoorPricingStrategy,
    GlassOverdoorPricingStrategy,
    GlassOverdoorCalculationStrategy,
    OverdoorAssetsCalculationStrategy,
    OverdoorAssetsPricingStrategy,
    OverdoorTotalPriceCalculationStrategy,
    OverdoorWithoutAssetsPriceCalculationStrategy,
    MountHardwarePricingStrategy,
    MountHardwareCalculationStrategy,
    MountCalculationStrategy,
    OverheadPricingStrategy,
    OverheadCalculationStrategy,
    OverheadTotalCalculationStrategy,
    ExtraPricingStrategy,
    ExtraCalculationStrategy,
    ExtraDiscountedPageTotalCalculationStrategy,
    DiscountSumCalculationStrategy,
    DiscountPercentCalculationStrategy,
    EstimateCalculationStrategy,
    DeliveryPricingStrategy,
    DeliveryCalculationStrategy,
    DoorCutsCalculationStrategy,
    InstallationSubtotalCalculationStrategy,
    InstallationOldSubtotalCalculationStrategy,
    InstallationTotalCalculationStrategy,
    InstallationTotalWithOverchargeCalculationStrategy,
    HalfInstallationCalculationStrategy,
    EstimateWithExtraCalculationStrategy,
    TotalDueCalculationStrategy,
    TaxSumCalculationStrategy,
    SubtotalEstimateReviewCalculationStrategy,
    OverheadSumWithDiscountCalculationStrategy,
    ServicesSumCalculationStrategy,
    HalfInstallationSumCalculationStrategy,
    SummaryTotalCalculationStrategy,
} from "@/app/domain/strategies";

import { PartitionPricingStrategy } from "../domain/strategies/pricing/partition-pricing.strategy";
import { PartitionCalculationStrategy } from "../domain/strategies/calucation/partition-calculation.strategy";
import { Hardware, MountHardware } from "../domain/entities/hardware";
import { AluminiumOverdoorCalculationStrategy } from "../domain/strategies/calucation/aluminium-overdoor-calculation.strategy";
import { AluminiumOverdoorPricingStrategy } from "../domain/strategies/pricing/aluminium-overdoor-pricing.strategy";
import { OverheadItem } from "../domain/entities/overhead";
import { ExtraTotalCalculationStrategy } from "../domain/strategies/calucation/extra-calculation.strategy";
import {
    SpaceCalculationStrategy,
    SpacesTotalCalculationStrategy,
} from "../domain/strategies/calucation/space-calculation.strategy";
import { SubtotalCalculationStrategy } from "../domain/strategies/calucation/subtotal-calculation.strategy";
import { DeliveryItem } from "../domain/entities/delivery";
import { OverdoorPricingStrategy } from "../domain/strategies/pricing/overdoor-pricing.strategy";
import { PartitionPagePricingStrategy } from "../domain/strategies/pricing/partition-page-pricing.strategy";
import { TransomHardwarePricingStrategy } from "../domain/strategies/pricing/transom-hardware-pricing.strategy";
import { TransomHardwareCalculationStrategy } from "../domain/strategies/calucation/transom-hardware-calculation.strategy";

type Constructor<T> = new (...args: unknown[]) => T;

export class PricingService {
    private estimateStore = useNewEstimateStore();
    private deliveryStore = useDeliveryStore();
    private partitionStore = usePartitionStore();
    private doorsStore = useDoorsStore();
    private overdoorStore = useOverdoorStore();
    private mountsStore = useMountsStore();
    private overheadStore = useOverheadStore();
    private extraStore = useExtraStore();
    private spacesStore = useSpacesStore();
    private installationStore = useInstallationStore();
    private estimateReviewStore = useEstimateReviewStore();

    public getPartitionGlassPrices() {
        const installedSystemCode = computed(
            () => this.estimateStore.estimate.installedSystemCode,
        );

        const stateCode = computed(
            () => this.estimateStore.estimate.shippingState,
        );

        const activePartition = computed(
            () => this.partitionStore.activePartitions,
        );

        return this.getPricesWithStrategy(
            GlassPricingStrategy,
            installedSystemCode.value,
            stateCode.value,
            activePartition.value,
        );
    }

    public calculatePartitionGlassPrice() {
        const activePartition = computed(
            () => this.partitionStore.activePartitions,
        );
        const glass = activePartition.value?.getActiveGlass();

        return this.calculatePriceWithStrategy(GlassCalculationStrategy, glass);
    }

    public getDoorGlassPrices() {
        const installedSystemCode = computed(
            () => this.estimateStore.estimate.installedSystemCode,
        );
        const stateCode = computed(
            () => this.estimateStore.estimate.shippingState,
        );
        const activeGlassDoor = computed(() => this.doorsStore.activeGlassDoor);
        const activeGlassThinknessDoor = computed(
            () => this.doorsStore.activeGlassThinknessDoor,
        );

        return this.getPricesWithStrategy(
            GlassDoorPricingStrategy,
            installedSystemCode.value,
            stateCode.value,
            activeGlassDoor.value?.structureType,
            activeGlassThinknessDoor.value?.structureType,
        );
    }

    public calculateDoorGlassPrice() {
        const activeGlassDoor = computed(() => this.doorsStore.activeGlassDoor);

        return this.calculatePriceWithStrategy(
            GlassDoorCalculationStrategy,
            activeGlassDoor.value!,
        );
    }

    public calculateOutOfSquarePrice() {
        const activeOutOfSquare = computed(
            () => this.partitionStore.activeOutOfSquare,
        );

        return this.calculatePriceWithStrategy(
            OutOfSquareCalculationStrategy,
            activeOutOfSquare.value!,
        );
    }

    public getOverdoorAssetsPrices() {
        return this.getPricesWithStrategy(OverdoorAssetsPricingStrategy);
    }

    public calculateOverdoorAssetsPrice() {
        const activeOverdoorAssets = computed(
            () => this.overdoorStore.activeOverdoorAssets,
        );

        return this.calculatePriceWithStrategy(
            OverdoorAssetsCalculationStrategy,
            activeOverdoorAssets.value!,
        );
    }

    public getOutOfSquarePrices() {
        return this.getPricesWithStrategy(OutOfSquarePricingStrategy);
    }

    public calculateOverdoorOutOfSquarePrice() {
        const activeOutOfSquare = computed(
            () => this.overdoorStore.activeOverdoorOutOfSquare,
        );

        return this.calculatePriceWithStrategy(
            OutOfSquareCalculationStrategy,
            activeOutOfSquare.value!,
        );
    }

    public calculatePartitionPrice(partition?: Partition) {
        const activePartition = computed(
            () => this.partitionStore.activePartitions,
        );

        return this.calculatePriceWithStrategy(
            PartitionCalculationStrategy,
            partition || activePartition.value!,
        );
    }

    public getPartitionPrices(partition?: Partition) {
        const activePartition = computed(
            () => this.partitionStore.activePartitions,
        );

        return this.getPricesWithStrategy(
            PartitionPricingStrategy,
            partition || activePartition.value,
        );
    }

    public getPartitionPagePrices(partition?: Partition) {
        const activePartition = computed(
            () => this.partitionStore.activePartitions,
        );

        return this.getPricesWithStrategy(
            PartitionPagePricingStrategy,
            partition || activePartition.value,
        );
    }

    public getHarwarePrices(hardware: Hardware) {
        const hardwareTypeCode = hardware.typeCode;
        const hardwareStructureType = hardware.structureType;
        return this.getPricesWithStrategy(
            DoorHardwarePricingStrategy,
            hardwareTypeCode,
            hardwareStructureType,
        );
    }

    public getTransomHarwarePrices(hardware: Hardware) {
        const hardwareTypeCode = hardware.typeCode;
        const hardwareStructureType = hardware.structureType;

        return this.getPricesWithStrategy(
            TransomHardwarePricingStrategy,
            hardwareTypeCode,
            hardwareStructureType,
        );
    }

    public calculateDoorHardwarePrice(hardware: Hardware) {
        return this.calculatePriceWithStrategy(
            DoorHardwareCalculationStrategy,
            hardware,
        );
    }

    public calculateTransomHardwarePrice(hardware: Hardware) {
        return this.calculatePriceWithStrategy(
            TransomHardwareCalculationStrategy,
            hardware,
        );
    }

    public getMountHarwarePrices(hardware: MountHardware) {
        const installedSystemCode = computed(
            () => this.estimateStore.estimate.installedSystemCode,
        );
        const colorCode = computed(() => this.estimateStore.estimate.colorCode);
        const hardwareTypeCode = hardware.typeCode;
        const hardwareStructureType = hardware.structureType;

        return this.getPricesWithStrategy(
            MountHardwarePricingStrategy,
            installedSystemCode,
            hardwareTypeCode,
            hardwareStructureType,
            colorCode,
        );
    }

    public calculateMountHardwarePrice(hardware: MountHardware) {
        return this.calculatePriceWithStrategy(
            MountHardwareCalculationStrategy,
            hardware,
        );
    }

    public calculateMountPrice() {
        const activeMount = computed(() => this.mountsStore.activeMount);

        return this.calculatePriceWithStrategy(
            MountCalculationStrategy,
            activeMount.value!,
        );
    }

    public getDoorCutsPrices() {
        return this.getPricesWithStrategy(DoorCutsPricingStrategy);
    }

    public calculateDoorCutsPrice(cut: DoorCut) {
        return this.calculatePriceWithStrategy(
            DoorCutsCalculationStrategy,
            cut,
        );
    }

    public calculateDoorPrice() {
        const activeDoor = computed(() => this.doorsStore.activeDoor);

        return this.calculatePriceWithStrategy(
            DoorCalculationStrategy,
            activeDoor.value!,
        );
    }

    public getDoorPrices() {
        const activeDoor = computed(() => this.doorsStore.activeDoor);

        return this.getPricesWithStrategy(
            DoorPricingStrategy,
            activeDoor.value!,
        );
    }

    public getOverdoorGlassPrices() {
        const installedSystemCode = computed(
            () => this.estimateStore.estimate.installedSystemCode,
        );
        const stateCode = computed(
            () => this.estimateStore.estimate.shippingState,
        );
        const activeGlassOverdoor = computed(() =>
            this.overdoorStore.activeOverdoorMaterials.at(0),
        );
        const activeGlassThinknessOverdoor = computed(() =>
            this.overdoorStore.activeOverdoorMaterials.at(1),
        );

        return this.getPricesWithStrategy(
            GlassOverdoorPricingStrategy,
            installedSystemCode.value,
            stateCode.value,
            activeGlassOverdoor.value?.structureType,
            activeGlassThinknessOverdoor.value?.structureType,
        );
    }

    public calculateOverdoorGlassPrice() {
        const activeGlassOverdoor = computed(
            () => this.overdoorStore.activeGlassOverdoor,
        );

        return this.calculatePriceWithStrategy(
            GlassOverdoorCalculationStrategy,
            activeGlassOverdoor.value!,
        );
    }

    public getOverdoorAluminiumPrices() {
        const activeAluminium = computed(() =>
            this.overdoorStore.activeOverdoorMaterials.at(0),
        );

        return this.getPricesWithStrategy(
            AluminiumOverdoorPricingStrategy,
            activeAluminium.value?.structureType,
        );
    }

    public calculateOverdoorAluminiumPrice() {
        const activeAluminiumMaterial = computed(() =>
            this.overdoorStore.activeOverdoorMaterials.at(0),
        );

        return this.calculatePriceWithStrategy(
            AluminiumOverdoorCalculationStrategy,
            activeAluminiumMaterial.value!,
        );
    }

    public getOverdoorPrice() {
        const activeOverdoor = computed(
            () => this.overdoorStore.activeOverdoor,
        );

        return this.getPricesWithStrategy(
            OverdoorPricingStrategy,
            activeOverdoor.value!,
        );
    }

    public calculateOverdoorTotalPrice() {
        const activeOverdoor = computed(
            () => this.overdoorStore.activeOverdoor,
        );

        return this.calculatePriceWithStrategy(
            OverdoorTotalPriceCalculationStrategy,
            activeOverdoor.value!,
        );
    }

    public calculateOverdoorWithoutAssetsPrice() {
        const activeOverdoor = computed(
            () => this.overdoorStore.activeOverdoor,
        );

        return this.calculatePriceWithStrategy(
            OverdoorWithoutAssetsPriceCalculationStrategy,
            activeOverdoor.value!,
        );
    }

    public getOverheadPrices(overheadItem: OverheadItem) {
        return this.getPricesWithStrategy(
            OverheadPricingStrategy,
            overheadItem.typeCode,
            overheadItem.structureType,
        );
    }

    public calculateOverheadPrice(overheadItem: OverheadItem) {
        const installedSystemCode = computed(
            () => this.estimateStore.estimate.installedSystemCode,
        );

        return this.calculatePriceWithStrategy(
            OverheadCalculationStrategy,
            overheadItem!,
            installedSystemCode.value,
        );
    }

    public calculateOverheadTotalPrice() {
        const activeOverhead = computed(
            () => this.overheadStore.activeOverhead,
        );

        return this.calculatePriceWithStrategy(
            OverheadTotalCalculationStrategy,
            activeOverhead.value!,
        );
    }

    public getExtraPrices(extraItem: ExtraItem) {
        return this.getPricesWithStrategy(
            ExtraPricingStrategy,
            extraItem.structureType,
        );
    }

    public calculateExtraPrice(extraItem: ExtraItem) {
        return this.calculatePriceWithStrategy(
            ExtraCalculationStrategy,
            extraItem!,
        );
    }

    public calculateExtraDiscountedPageTotal() {
        const activePartitionsExtra = computed(
            () => this.partitionStore.activePartitions?.getExtra(),
        );

        return this.calculatePriceWithStrategy(
            ExtraDiscountedPageTotalCalculationStrategy,
            // @ts-ignore
            null,
            activePartitionsExtra.value,
        );
    }

    public getDeliveryPrices(deliveryItem: DeliveryItem) {
        return this.getPricesWithStrategy(
            DeliveryPricingStrategy,
            deliveryItem.typeCode,
            deliveryItem.structureType,
        );
    }

    public calculateDeliveryPrice(deliveryItem: DeliveryItem) {
        const numberOfTracks = computed(
            () => this.deliveryStore.numberOfTracks,
        );
        const deliveryType = computed(() => this.deliveryStore.deliveryType);

        return this.calculatePriceWithStrategy(
            DeliveryCalculationStrategy,
            deliveryItem,
            numberOfTracks.value,
            deliveryType.value,
        );
    }

    public calculateExtraTotalPrice(extraItems: ExtraItem[] = []) {
        return this.calculatePriceWithStrategy(
            ExtraTotalCalculationStrategy,
            extraItems,
        );
    }

    public calculateSpacePrice(space?: Space) {
        const activeSpace = computed(() => this.spacesStore.activeSpace);

        return this.calculatePriceWithStrategy(
            SpaceCalculationStrategy,
            space || activeSpace.value!,
        );
    }

    public calculateSpaceTotalPrice() {
        const spaces = computed(() => this.spacesStore.spaces);

        return this.calculatePriceWithStrategy(
            SpacesTotalCalculationStrategy,
            spaces.value,
        );
    }

    public calculateSubtotalPrice() {
        const spaces = computed(() => this.spacesStore.spaces);
        const multiplicator = computed(
            () => this.estimateStore.estimate.multiplicator,
        );
        const discountedExtraTotal = computed(
            () => this.extraStore.discountedExtraTotal,
        );

        return this.calculatePriceWithStrategy(
            SubtotalCalculationStrategy,
            spaces.value,
            multiplicator.value as number,
            discountedExtraTotal.value,
        );
    }
    public calculateInstallationSubtotalPrice() {
        const installationItems = computed(
            () => this.installationStore.installationItemList,
        );

        return this.calculatePriceWithStrategy(
            InstallationSubtotalCalculationStrategy,
            installationItems.value,
        );
    }

    public calculateInstallationOldSubtotalPrice() {
        const installationItems = computed(
            () => this.installationStore.installationItemList,
        );

        return this.calculatePriceWithStrategy(
            InstallationOldSubtotalCalculationStrategy,
            installationItems.value,
        );
    }

    public calculateInstallationPriceWithDiscount() {
        const discountSum = computed(() => this.installationStore.discountSum);
        const subtotalPrice = computed(
            () => this.installationStore.subtotalPrice,
        );

        return this.calculatePriceWithStrategy(
            InstallationTotalCalculationStrategy,
            // @ts-ignore
            null,
            subtotalPrice.value,
            discountSum.value,
        );
    }

    public calculateInstallationPriceWithOvercharge() {
        const overChargeSum = computed(
            () => this.installationStore.overChargeSum,
        );
        const priceWithDiscount = computed(
            () => this.installationStore.priceWithDiscount,
        );
        const isOverchargeSelected = computed(
            () => this.installationStore.isOverchargeSelected,
        );

        return this.calculatePriceWithStrategy(
            InstallationTotalWithOverchargeCalculationStrategy,
            // @ts-ignore
            null,
            overChargeSum.value,
            priceWithDiscount.value,
            isOverchargeSelected.value,
        );
    }
    public calculateHalfInstallation() {
        const overChargeSum = computed(
            () => this.installationStore.overChargeSum,
        );
        const priceWithDiscount = computed(
            () => this.installationStore.priceWithDiscount,
        );
        const isOverchargeSelected = computed(
            () => this.installationStore.isOverchargeSelected,
        );

        return this.calculatePriceWithStrategy(
            HalfInstallationSumCalculationStrategy,
            // @ts-ignore
            null,
            priceWithDiscount.value,
            overChargeSum.value,
            isOverchargeSelected.value,
        );
    }

    public calculateInstallationTotalSum() {
        const priceWithDiscount = computed(
            () => this.installationStore.priceWithDiscount,
        );
        const halfInstallationSum = computed(
            () => this.installationStore.halfInstallationSum,
        );

        const overChargeSum = computed(
            () => this.installationStore.overChargeSum,
        );
        const isOverchargeSelected = computed(
            () => this.installationStore.isOverchargeSelected,
        );
        const systemCode = computed(
            () => this.estimateStore.estimate.installedSystemCode,
        );

        return this.calculatePriceWithStrategy(
            HalfInstallationCalculationStrategy,
            // @ts-ignore
            null,
            priceWithDiscount.value,
            halfInstallationSum.value,
            overChargeSum.value,
            isOverchargeSelected.value,
            systemCode.value,
        );
    }

    public calculateDiscountSum() {
        const subtotal = computed(() => this.estimateStore.estimate.subtotal);
        const discoutPercent = computed(
            () => this.estimateStore.estimate.discountPercent,
        );
        const dealType = computed(
            () => this.estimateStore.estimate.dealTypeCode,
        );

        const prices = this.calculatePriceWithStrategy(
            DiscountSumCalculationStrategy,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // component for this strategy not needed
            null,
            discoutPercent.value,
            subtotal.value,
        );

        const pricing = new Pricing(
            prices.price,
            prices.priceB2C,
            prices.priceB2B,
        );

        return pricing.getSystemPrice(dealType.value).getValue();
    }

    public calculateDiscountPercent() {
        const subtotal = computed(() => this.estimateStore.estimate.subtotal);
        const discountSum = computed(
            () => this.estimateStore.estimate.discountSum,
        );
        const dealType = computed(
            () => this.estimateStore.estimate.dealTypeCode,
        );

        const prices = this.calculatePriceWithStrategy(
            DiscountPercentCalculationStrategy,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // component for this strategy not needed
            null,
            discountSum.value,
            subtotal.value,
        );

        const pricing = new Pricing(
            prices.price,
            prices.priceB2C,
            prices.priceB2B,
        );

        return pricing.getSystemPrice(dealType.value).getValue();
    }

    public calculateTaxSumPrice() {
        const subtotal = computed(() => this.estimateReviewStore.subtotalPrice);
        const partitionPrice = computed(
            () => this.estimateStore.estimate.estimatePrice,
        );
        const taxPercent = computed(
            () => this.estimateStore.estimate.taxPercent,
        );
        const taxStatus = computed(() => this.estimateStore.estimate.taxStatus);

        return this.calculatePriceWithStrategy(
            TaxSumCalculationStrategy,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // component for this strategy not needed
            null,
            subtotal.value,
            partitionPrice.value,
            taxPercent.value,
            taxStatus.value,
        );
    }

    public calculateTotalDuePrice() {
        const subtotal = computed(() => this.estimateReviewStore.subtotalPrice);
        const taxSum = computed(() => this.estimateStore.estimate.taxSum);

        return this.calculatePriceWithStrategy(
            TotalDueCalculationStrategy,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // component for this strategy not needed
            null,
            taxSum.value,
            subtotal.value,
        );
    }

    public calculateSubtotalEstimateReviewPrice() {
        const estimatePrice = computed(
            () => this.estimateStore.estimate.estimatePrice,
        );
        const noDiscountExtraTotal = computed(() => this.extraStore.extraTotal);
        const installationPrice = computed(
            () => this.installationStore.totalInstallationSum,
        );

        const deliveryPrice = computed(
            () => this.deliveryStore.totalDeliveryPrice,
        );
        const deliveryType = computed(() => this.deliveryStore.deliveryType);
        const isIncludeCombinedDeliveryInPrice = computed(
            () => this.deliveryStore.isIncludeCombinedDeliveryInPrice,
        );

        return this.calculatePriceWithStrategy(
            SubtotalEstimateReviewCalculationStrategy,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // component for this strategy not needed
            null,
            estimatePrice.value,
            installationPrice.value,
            deliveryPrice.value,
            deliveryType.value,
            isIncludeCombinedDeliveryInPrice.value,
            noDiscountExtraTotal.value,
        );
    }

    public calculateOverheadSumWithDiscount() {
        const activeOverhead = computed(
            () => this.overheadStore.activeOverhead,
        );
        const discountPercent = computed(
            () => this.estimateStore.estimate.discountPercent,
        );

        return this.calculatePriceWithStrategy(
            OverheadSumWithDiscountCalculationStrategy,
            activeOverhead.value,
            discountPercent.value,
        );
    }

    public calculateServicesSumWithDiscount() {
        const deliverySum = computed(
            () => this.estimateStore.estimate.deliverySum,
        );
        const discountPercent = computed(
            () => this.estimateStore.estimate.discountPercent,
        );
        const isIncludeCombinedDeliveryInPrice = computed(
            () => this.deliveryStore.isIncludeCombinedDeliveryInPrice,
        );
        const halfInstallation = computed(
            () => this.installationStore.halfInstallationSum,
        );
        const isHalfInstallation = computed(
            () => this.estimateStore.estimate.isHalfInstallation,
        );

        return this.calculatePriceWithStrategy(
            ServicesSumCalculationStrategy,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // component for this strategy not needed
            null,
            deliverySum.value,
            discountPercent.value,
            isIncludeCombinedDeliveryInPrice.value,
            halfInstallation.value,
            isHalfInstallation.value,
        );
    }

    public calculateEstimatePrice() {
        const subtotal = computed(() => this.estimateStore.estimate.subtotal);
        const discountSum = computed(
            () => this.estimateStore.estimate.discountSum,
        );

        return this.calculatePriceWithStrategy(
            EstimateCalculationStrategy,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // component for this strategy not needed
            null,
            discountSum.value,
            subtotal.value,
        );
    }
    public calculateEstimatePriceWithExtra() {
        const estimatePriceWithoutExtra = computed(
            () => this.estimateStore.estimate.estimatePriceWithoutExtra,
        );
        const extraTotal = computed(() => this.extraStore.extraTotal);

        return this.calculatePriceWithStrategy(
            EstimateWithExtraCalculationStrategy,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // component for this strategy not needed
            null,
            estimatePriceWithoutExtra.value,
            extraTotal.value,
        );
    }

    public calculateCurrentSummaryTotalPrice() {
        const spaces = computed(() => this.spacesStore.spaces);
        const totalInstallationSum = computed(() => this.installationStore.totalInstallationSum);
        const totalDeliveryPrice = computed(() => this.deliveryStore.totalDeliveryPrice);

        return this.calculatePriceWithStrategy(
            SummaryTotalCalculationStrategy,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // component for this strategy not needed
            null,
            spaces.value,
            totalInstallationSum.value,
            totalDeliveryPrice.value,
        );
    }

    private getPricesWithStrategy<T extends IPricingStrategy>(
        Strategy: Constructor<T>,
        ...args: unknown[]
    ): PriceResult {
        const strategy = new Strategy();
        return strategy.getPrices(...args);
    }

    private calculatePriceWithStrategy<T extends ICalculationStrategy>(
        Strategy: Constructor<T>,
        component: ReactiveComponent | ReactiveComponent[],
        ...args: any[]
    ): PriceResult {
        const strategy = new Strategy();
        return strategy.calculate(component, args);
    }
}
